import { ParamMap, Params } from '@angular/router';
import { NewsFeedChoice, NewsFeedDefinition, NewsFeedState, NewsFilter, NewsSort } from './news.model';

/**
 * A news page keeps its whole state in its query string, so a link or a reload
 * rebuilds the same page. The page's parameters are the API's without the
 * `filter[…]` wrapper — `?application=culturequestion&tag=news:big&page=2`
 * — and a parameter left out means the feed's default for that filter.
 *
 * A filter the feed does not let the reader choose is always the default,
 * whatever the URL says, so a hand-edited link cannot widen a site's feed.
 */
export function readNewsFeedState(params: ParamMap, feed: NewsFeedDefinition): NewsFeedState {
  const filter: NewsFilter = { ...feed.defaultFilter };

  if (mayChoose(feed, 'application')) {
    const applications = list(params.get('application'));
    if (applications) {
      filter.application = applications;
    }
  }

  if (mayChoose(feed, 'tag')) {
    const tags = list(params.get('tag'));
    if (tags) {
      filter.tag = tags;
    }
  }

  if (mayChoose(feed, 'search')) {
    const search = params.get('search')?.trim();
    if (search && search.length >= 3) {
      filter.search = search;
    }
  }

  const sort: NewsSort = mayChoose(feed, 'sort') && params.get('sort') === 'published_at' ? 'published_at' : '-published_at';
  const page = Math.max(1, parseInt(params.get('page') ?? '1', 10) || 1);

  return { filter, sort, page };
}

/**
 * The query string for a state: only what differs from the feed's default, and
 * `null` for the rest, so navigating with it clears what no longer applies.
 */
export function newsFeedQueryParams(state: NewsFeedState, feed: NewsFeedDefinition): Params {
  const defaults = feed.defaultFilter;

  return {
    application: sameList(state.filter.application, defaults.application) ? null : joined(state.filter.application),
    tag: sameList(state.filter.tag, defaults.tag) ? null : joined(state.filter.tag),
    search: state.filter.search && state.filter.search !== defaults.search ? state.filter.search : null,
    sort: state.sort === '-published_at' ? null : state.sort,
    page: state.page > 1 ? state.page : null,
  };
}

/**
 * Has the reader narrowed the feed beyond its default — by application, tag or
 * search? The sort and the page narrow nothing.
 */
export function hasChosenFilter(state: NewsFeedState, feed: NewsFeedDefinition): boolean {
  const params = newsFeedQueryParams(state, feed);

  return params['application'] !== null || params['tag'] !== null || params['search'] !== null;
}

export function mayChoose(feed: NewsFeedDefinition, choice: NewsFeedChoice): boolean {
  return feed.readerMayChoose.includes(choice);
}

/**
 * One value in or out of a list filter. An emptied list falls back to the
 * default, and choosing a value replaces "any" (`*`) rather than joining it,
 * since the API refuses `*` beside anything else.
 */
export function toggled(current: string[] | undefined, value: string, on: boolean, fallback: string[] | undefined): string[] | undefined {
  const values = (current ?? []).filter((v) => v !== '*' && v !== value);
  const next = on ? [...values, value] : values;

  return next.length ? next : fallback;
}

function list(value: string | null): string[] | undefined {
  const values = (value ?? '')
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean);

  return values.length ? values : undefined;
}

function joined(values: string[] | undefined): string | null {
  return values?.length ? values.join(',') : null;
}

function sameList(a: string[] | undefined, b: string[] | undefined): boolean {
  return (a ?? []).join(',') === (b ?? []).join(',');
}
